/**
 * The video ladder as the *manifest* declares it, independent of what this browser can play.
 *
 * The player only ever hands back renditions it is prepared to play, and it drops the rest before
 * they become tracks -- encrypted HEVC is the case that prompted this: Chrome decodes `hvc1` fine
 * in the clear, but Widevine offers no `hvc1` video capability at all, so every HEVC variant is
 * discarded and the preview silently claimed the encode had none.
 *
 * A preview player exists to confirm what was encoded, so the list has to come from the manifest
 * and be *annotated* with playability, rather than being whatever survived the player's filter.
 */

/** One video rendition as declared, shaped like the player's own so both can share a label. */
export interface ManifestRendition {
  codec: string | null;
  width: number | null;
  height: number | null;
  bandwidth: number | null;
  /**
   * An HLS variant whose CODECS lists audio codecs only (VTK-3665): positively not video. Missing CODECS or
   * RESOLUTION do not make a variant audio-only -- both are optional, and a video variant may omit them.
   */
  audioOnly?: boolean;
}

/** A manifest rendition plus whether this browser can actually play it. */
export interface VideoChoice extends ManifestRendition {
  /** The player's rendition id, or null when the player never offered it. */
  id: number | null;
  playable: boolean;
}

function num(value: string | undefined | null): number | null {
  const parsed = Number(value);
  return value !== undefined && value !== null && value !== '' && Number.isFinite(parsed) ? parsed : null;
}

/**
 * Attributes of one `#EXT-X-STREAM-INF` line.
 *
 * Split on commas that are not inside quotes: `CODECS="avc1.64001e,mp4a.40.2"` carries commas of
 * its own, and a naive split loses the second half of every multi-codec variant.
 */
function hlsAttributes(line: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  let field = '';
  let quoted = false;
  const push = () => {
    const at = field.indexOf('=');
    if (at > 0) {
      attrs[field.slice(0, at).trim()] = field.slice(at + 1).trim().replace(/^"|"$/g, '');
    }
    field = '';
  };
  for (const ch of line) {
    if (ch === '"') { quoted = !quoted; field += ch; } else if (ch === ',' && !quoted) { push(); } else { field += ch; }
  }
  push();
  return attrs;
}

/**
 * Video variants of an HLS master playlist, in playlist order.
 *
 * `#EXT-X-I-FRAME-STREAM-INF` is deliberately skipped: those are trick-play, the same thing the
 * DASH side marks with a trickmode EssentialProperty, and they are not something to offer a viewer.
 */
export function parseHlsVariants(text: string): ManifestRendition[] {
  return (text ?? '').split(/\r?\n/)
    .filter(line => line.startsWith('#EXT-X-STREAM-INF:'))
    .map(line => {
      const attrs = hlsAttributes(line.slice('#EXT-X-STREAM-INF:'.length));
      const [width, height] = (attrs['RESOLUTION'] ?? '').split('x');
      // CODECS lists every codec in the variant; the video one is whichever is not an audio fourcc.
      const codecs = (attrs['CODECS'] ?? '').split(',').map(c => c.trim()).filter(c => !!c);
      const codec = codecs.find(c => !/^(mp4a|ec-3|ac-3|opus|alac|flac)/i.test(c)) ?? null;
      const variant: ManifestRendition = {codec, width: num(width), height: num(height), bandwidth: num(attrs['BANDWIDTH'])};
      if (codecs.length && !codec && !variant.width && !variant.height) {
        variant.audioOnly = true;
      }
      return variant;
    });
}

/**
 * Video representations of a DASH manifest, in document order.
 *
 * Trick-play AdaptationSets are skipped by their `trickmode` EssentialProperty rather than by
 * frame rate: the rate only happens to be low, the descriptor is what actually says so.
 */
export function parseDashRepresentations(text: string): ManifestRendition[] {
  const doc = new DOMParser().parseFromString(text ?? '', 'application/xml');
  if (doc.querySelector('parsererror')) {
    return [];
  }
  const out: ManifestRendition[] = [];
  for (const set of Array.from(doc.getElementsByTagName('AdaptationSet'))) {
    const reps = Array.from(set.getElementsByTagName('Representation'));
    const mime = set.getAttribute('mimeType') ?? reps[0]?.getAttribute('mimeType') ?? '';
    const isVideo = set.getAttribute('contentType') === 'video' || mime.startsWith('video/');
    if (!isVideo) {
      continue;
    }
    const trickPlay = Array.from(set.getElementsByTagName('EssentialProperty'))
      .some(p => (p.getAttribute('schemeIdUri') ?? '').includes('trickmode'));
    if (trickPlay) {
      continue;
    }
    for (const rep of reps) {
      out.push({
        codec: rep.getAttribute('codecs') ?? set.getAttribute('codecs'),
        width: num(rep.getAttribute('width') ?? set.getAttribute('width')),
        height: num(rep.getAttribute('height') ?? set.getAttribute('height')),
        bandwidth: num(rep.getAttribute('bandwidth')),
      });
    }
  }
  return out;
}

/** Leading fourcc of a codec string, lowercased: "avc1.64001e" -> "avc1". */
function fourcc(codec: string | null | undefined): string {
  return String(codec ?? '').split('.')[0].toLowerCase();
}

/**
 * Pair each declared rendition with the player's, so the list is complete and still selectable.
 *
 * Matched on codec family plus resolution rather than on bandwidth: DASH reports a representation's
 * own bandwidth while an HLS variant's BANDWIDTH includes its audio, so the numbers legitimately
 * differ between the manifest and the player for the same rendition. Codec and resolution together
 * are unique within one ladder -- which is exactly why the label states both.
 *
 * A player rendition with no counterpart is appended rather than dropped: the manifest is the
 * better list, but never at the price of hiding something that plays.
 */
export function mergeWithPlayable(declared: ManifestRendition[], playable: any[]): VideoChoice[] {
  const remaining = [...(playable ?? [])];
  const key = (r: any) => `${fourcc(r?.codec)}/${r?.width ?? '?'}x${r?.height ?? '?'}`;

  const choices: VideoChoice[] = (declared ?? []).map(entry => {
    const at = remaining.findIndex(r => key(r) === key(entry));
    const match = at >= 0 ? remaining.splice(at, 1)[0] : null;
    return {
      // Prefer the player's own numbers where it has them -- they are what playback will use.
      codec: match?.codec ?? entry.codec,
      width: match?.width ?? entry.width,
      height: match?.height ?? entry.height,
      bandwidth: entry.bandwidth ?? match?.bandwidth ?? null,
      id: match ? match.id : null,
      playable: !!match,
    };
  });

  for (const left of remaining) {
    choices.push({
      codec: left?.codec ?? null,
      width: left?.width ?? null,
      height: left?.height ?? null,
      bandwidth: left?.bandwidth ?? null,
      id: left?.id ?? null,
      playable: true,
    });
  }
  return choices;
}

// ── The combined, always-visible ladder ──────────────────────────────────────

/** One row of the ladder panel: a rendition of one manifest, with why it can or cannot play. */
export interface LadderEntry extends ManifestRendition {
  /** Which manifest declared it -- the same content appears under both for shared codecs. */
  streamType: string;
  /** The player's rendition id, once that stream type is loaded. Null otherwise. */
  id: number | null;
  playable: boolean;
  /** Why not, when `playable` is false. Empty when it plays. */
  reason: string;
  /**
   * A stand-in row for a manifest that declares audio but no video (vtkpy's audio-only preview, VTK-3665):
   * the playing row is where the audio and subtitle selection lives, so an audio-only preview needs one.
   */
  noVideo?: boolean;
}

/** Does the manifest declare any audio? Only asked when it declares no video. */
function declaresAudio(text: string, hls: boolean): boolean {
  if (hls) {
    // The variant parser's own audio-only decision, so the two never disagree on which codecs are audio.
    return /^#EXT-X-MEDIA:.*TYPE=AUDIO/m.test(text ?? '') || parseHlsVariants(text).some(v => v.audioOnly);
  }
  const doc = new DOMParser().parseFromString(text ?? '', 'application/xml');
  return !doc.querySelector('parsererror') && Array.from(doc.getElementsByTagName('AdaptationSet')).some(set => {
    const mime = set.getAttribute('mimeType') ?? set.getElementsByTagName('Representation')[0]?.getAttribute('mimeType') ?? '';
    return set.getAttribute('contentType') === 'audio' || mime.startsWith('audio/');
  });
}

/**
 * The browser capability checks a player makes, injectable so they can be tested without one.
 *
 * Two questions, not one, and the difference is the whole reason HEVC went missing: whether the
 * *decoder* exists is `isTypeSupported`, whether a *key system* will hand out keys for it is
 * `requestMediaKeySystemAccess`. Chrome answers yes to the first for HEVC and no to the second, so
 * asking only the first would mark encrypted HEVC playable and asking only the second would mark
 * clear content unplayable.
 */
export interface CodecProbe {
  isTypeSupported(contentType: string): boolean;
  requestKeySystem(keySystem: string, config: any[]): Promise<unknown>;
}

export const browserProbe: CodecProbe = {
  isTypeSupported: (contentType: string) => {
    try {
      return !!(window as any).MediaSource?.isTypeSupported(contentType);
    } catch {
      return false;
    }
  },
  requestKeySystem: (keySystem: string, config: any[]) =>
    navigator.requestMediaKeySystemAccess(keySystem, config as any),
};

/**
 * Key systems to try, in no meaningful order -- any one of them playing the codec is enough.
 *
 * A browser has at most one or two of these, so most calls reject and that is not an error. FairPlay
 * appears under two ids because Safari has moved between them.
 */
const KEY_SYSTEMS = [
  'com.widevine.alpha',
  'com.microsoft.playready',
  'com.apple.fps',
  'com.apple.fps.1_0',
];

export function contentTypeOf(codec: string | null): string {
  return `video/mp4; codecs="${codec ?? ''}"`;
}

/**
 * Can this browser play a rendition of this codec, and if not, why?
 *
 * Answers per codec string, not per rendition: within one ladder every rendition of a codec stands
 * or falls together, and the probe is the expensive part.
 */
export async function probeCodec(
  codec: string | null,
  encrypted: boolean,
  probe: CodecProbe = browserProbe,
): Promise<{playable: boolean; reason: string}> {
  if (!codec) {
    return {playable: false, reason: 'the manifest states no codec'};
  }
  if (!probe.isTypeSupported(contentTypeOf(codec))) {
    return {playable: false, reason: 'this browser has no decoder for it'};
  }
  if (!encrypted) {
    return {playable: true, reason: ''};
  }
  for (const keySystem of KEY_SYSTEMS) {
    try {
      await probe.requestKeySystem(keySystem, [{
        initDataTypes: ['cenc', 'sinf', 'skd'],
        videoCapabilities: [{contentType: contentTypeOf(codec)}],
      }]);
      return {playable: true, reason: ''};
    } catch {
      // Not this one. A browser only has one or two, so most of these reject by design.
    }
  }
  // The decoder exists but no DRM will feed it. Encrypted HEVC in Chrome is exactly this.
  return {playable: false, reason: 'no DRM in this browser will decrypt this codec'};
}

/**
 * Build the full ladder across both manifests, with playability resolved per codec.
 *
 * Rows are grouped by stream type and then kept in manifest order, so the panel reads the way the
 * ladder was written rather than sorted into something the operator has to re-map.
 */
export async function buildLadder(
  manifests: {streamType: string; text: string; hls: boolean}[],
  encrypted: boolean,
  probe: CodecProbe = browserProbe,
): Promise<LadderEntry[]> {
  const support = new Map<string, {playable: boolean; reason: string}>();
  const entries: LadderEntry[] = [];

  for (const manifest of manifests) {
    // An HLS variant whose CODECS lists audio only is not a video rendition; one that merely omits CODECS
    // and RESOLUTION stays listed (both attributes are optional).
    const declared = (manifest.hls ? parseHlsVariants(manifest.text) : parseDashRepresentations(manifest.text))
      .filter(r => !r.audioOnly);
    if (!declared.length && declaresAudio(manifest.text, manifest.hls)) {
      entries.push({
        codec: null, width: null, height: null, bandwidth: null,
        streamType: manifest.streamType, id: null, playable: true, reason: '', noVideo: true,
      });
      continue;
    }
    for (const rendition of declared) {
      const codec = rendition.codec ?? '';
      if (!support.has(codec)) {
        support.set(codec, await probeCodec(rendition.codec, encrypted, probe));
      }
      const verdict = support.get(codec);
      entries.push({...rendition, streamType: manifest.streamType, id: null, ...verdict});
    }
  }
  return entries;
}

/**
 * Attach the ids of the stream type that is currently loaded, so its rows become selectable.
 *
 * Only that stream type gets ids: the other manifest is not loaded, so nothing in it can be
 * switched to without a reload. Its rows stay listed and stay honest about playability.
 */
export function attachPlayerIds(
  ladder: LadderEntry[],
  streamType: string,
  playable: any[],
): LadderEntry[] {
  const remaining = [...(playable ?? [])];
  const key = (r: any) =>
    `${String(r?.codec ?? '').split('.')[0].toLowerCase()}/${r?.width ?? '?'}x${r?.height ?? '?'}`;
  return ladder.map(entry => {
    if (entry.streamType !== streamType) {
      return {...entry, id: null};
    }
    const at = remaining.findIndex(r => key(r) === key(entry));
    const match = at >= 0 ? remaining.splice(at, 1)[0] : null;
    return {...entry, id: match ? match.id : null};
  });
}
